/**
 * A delivery log — what was emailed / messaged, to whom, and how it went. The cloud's (every
 * alert it sent: a site's AI, system and offline alerts, and its own) and a site's (what the site
 * sent itself or handed to the cloud) — each app maps its rows into DeliveryLogEntry.
 *
 * "Sent to" is who it actually went to (the channel results), not who it was meant for.
 */
import type { ComponentType } from 'react';
import { CloudIcon, CpuChipIcon, ServerStackIcon, SignalSlashIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { Callout, StatusPill } from '../common/Callout';
import type { DeliveryKind, DeliveryLogEntry } from './alertActionsModel';

export interface DeliveryLogTableProps {
  entries: DeliveryLogEntry[] | null;
  /** Cloud log: a Site column. */
  showSite?: boolean;
  /** Site log: whether the site sent it or handed it to the cloud. */
  showVia?: boolean;
  emptyText?: string;
  error?: string;
}

export const DELIVERY_KIND: Record<DeliveryKind, { label: string; icon: ComponentType<{ className?: string }> }> = {
  ai: { label: 'AI analytics', icon: CpuChipIcon },
  system: { label: 'System', icon: ServerStackIcon },
  offline: { label: 'Site offline', icon: SignalSlashIcon },
  cloud: { label: 'Cloud', icon: CloudIcon },
};

const STATUS_TONE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  sent: 'success', partial: 'warning', failed: 'danger', pending: 'neutral', skipped: 'neutral', queued: 'warning',
  silenced: 'neutral', sending: 'neutral',
};
const STATUS_HINT: Record<string, string> = {
  skipped: 'Nobody to send to — no recipients, or no channel on',
  silenced: 'A pause was in force — the alert is recorded, nobody was notified',
  sending: 'Being sent right now',
  queued: 'Some recipients failed for a passing reason (server busy, network down, the cloud unreachable) — tried again automatically',
};
const timeOf = (at: string) => new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/** Under a queued row: when it is tried next, or that the cloud is retrying it. */
function retryNote(row: DeliveryLogEntry): string | null {
  if (row.status !== 'queued') return null;
  if (row.retry_at) return `try ${(row.attempts || 1) + 1} at ${timeOf(row.retry_at)}`;
  return row.via === 'cloud' ? 'the cloud is retrying — see its log' : null;
}
const channelName = (c: string | null) => (c === 'email' ? 'Email' : c === 'message_api' ? 'Message API' : c === 'dashboard' ? 'Pop-up' : 'Delivery');

export default function DeliveryLogTable({ entries, showSite, showVia, emptyText = 'Nothing sent yet.', error }: DeliveryLogTableProps) {
  if (error) return <div className="p-3"><Callout tone="danger">{error}</Callout></div>;
  if (entries === null) {
    return <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <div key={i} className="h-9 animate-pulse rounded-lg bg-muted" />)}</div>;
  }
  if (entries.length === 0) return <p className="px-4 py-10 text-center text-[13px] text-muted-foreground">{emptyText}</p>;

  const heads = ['Time', ...(showSite ? ['Site'] : []), 'Type', 'Alert', 'Camera / service', ...(showVia ? ['Sent by'] : []), 'Sent to', 'Result'];
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="border-b border-border bg-muted">
          <tr>
            {heads.map((h) => (
              <th key={h} className="whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {entries.map((row) => {
            const results = Array.isArray(row.results) ? row.results : [];
            const errors = results.filter((r) => !r.ok && r.error).map((r) => `${channelName(r.channel)}: ${r.error}`);
            const kind = DELIVERY_KIND[row.kind] || DELIVERY_KIND.system;
            const KindIcon = kind.icon;
            // Who it reached: the channel results; before a send (or on old rows), who it was meant for.
            const sentTo = results.filter((r) => r.channel && (r.recipients || []).length).length
              ? results.filter((r) => r.channel).map((r) => [r.channel as string, r.recipients || []] as const)
              : Object.entries(row.requested || {});
            return (
              <tr key={row.id} className="align-top hover:bg-muted">
                <td className="whitespace-nowrap px-3 py-2.5 text-[12px] text-muted-foreground">{new Date(row.at).toLocaleString()}</td>
                {showSite && <td className="px-3 py-2.5 text-[13px] text-foreground">{row.site || '—'}</td>}
                <td className="whitespace-nowrap px-3 py-2.5 text-[12px] text-foreground">
                  <span className="inline-flex items-center gap-1"><KindIcon className="h-3.5 w-3.5 text-muted-foreground" />{kind.label}</span>
                </td>
                <td className="max-w-[16rem] px-3 py-2.5">
                  <div className="truncate text-[13px] text-foreground">{row.title}</div>
                  {row.detail && <div className="truncate text-[11px] text-muted-foreground">{row.detail}</div>}
                </td>
                <td className="px-3 py-2.5 text-[12px] text-foreground">{row.source || '—'}</td>
                {showVia && (
                  <td className="whitespace-nowrap px-3 py-2.5 text-[12px] text-foreground">
                    <span className="inline-flex items-center gap-1">
                      {row.via === 'cloud' ? <CloudIcon className="h-3.5 w-3.5 text-muted-foreground" /> : <BuildingOffice2Icon className="h-3.5 w-3.5 text-muted-foreground" />}
                      {row.via === 'cloud' ? 'Cloud' : 'This site'}
                    </span>
                  </td>
                )}
                <td className="max-w-[16rem] px-3 py-2.5 text-[12px] text-foreground">
                  {sentTo.length === 0 ? '—' : sentTo.map(([c, list]) => (
                    <div key={c} className="truncate" title={list.join(', ')}>
                      <span className="text-muted-foreground">{channelName(c)}:</span> {list.length ? list.join(', ') : 'nobody'}
                    </div>
                  ))}
                </td>
                <td className="min-w-[14rem] max-w-[22rem] px-3 py-2.5">
                  <span title={STATUS_HINT[row.status]}><StatusPill tone={STATUS_TONE[row.status] || 'neutral'}>{row.status === 'queued' ? 'retrying' : row.status}</StatusPill></span>
                  {retryNote(row) && <div className="mt-1 text-[11px] text-muted-foreground">{retryNote(row)}</div>}
                  {row.status !== 'queued' && (row.attempts || 1) > 1 && <div className="mt-1 text-[11px] text-muted-foreground">after {row.attempts} tries</div>}
                  {errors.length > 0 && <div className="mt-1 text-[11px] text-destructive">{errors.join(' · ')}</div>}
                  {row.note && <div className="mt-1 text-[11px] text-muted-foreground">{row.note}</div>}
                  {row.duration_ms != null && row.status !== 'silenced' && <div className="mt-1 text-[11px] text-muted-foreground">took {row.duration_ms < 1000 ? `${row.duration_ms} ms` : `${(row.duration_ms / 1000).toFixed(1)} s`}</div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
